import { useCallback, useEffect, useState } from 'react'
import { browseConnector } from '../../api/connectors'
import type { BrowseNode } from '../../types/api'

interface FileTreeBrowserProps {
  connectorId: number
  selectedPaths: Set<string>
  onSelectionChange: (paths: Set<string>) => void
}

interface TreeNodeData {
  node: BrowseNode
  children: TreeNodeData[] | null
  loading: boolean
}

export function FileTreeBrowser({
  connectorId,
  selectedPaths,
  onSelectionChange,
}: FileTreeBrowserProps) {
  const [roots, setRoots] = useState<TreeNodeData[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadRoots = useCallback(async () => {
    setInitialLoading(true)
    setError(null)
    try {
      const nodes = await browseConnector(connectorId, null)
      setRoots(nodes.map((n) => ({ node: n, children: null, loading: false })))
    } catch {
      setError('Failed to load repository contents')
    } finally {
      setInitialLoading(false)
    }
  }, [connectorId])

  useEffect(() => {
    loadRoots()
  }, [loadRoots])

  const loadChildren = useCallback(
    async (nodeId: string) => {
      setRoots((prev) => setNodeLoading(prev, nodeId, true))
      try {
        const children = await browseConnector(connectorId, nodeId)
        setRoots((prev) =>
          setNodeChildren(
            prev,
            nodeId,
            children.map((n) => ({ node: n, children: null, loading: false }))
          )
        )
      } catch {
        setRoots((prev) => setNodeLoading(prev, nodeId, false))
      }
    },
    [connectorId]
  )

  function toggleExpand(node: BrowseNode) {
    if (!node.hasChildren) return

    const next = new Set(expanded)
    if (next.has(node.id)) {
      next.delete(node.id)
    } else {
      next.add(node.id)
      // Load children if not yet loaded
      const existing = findNode(roots, node.id)
      if (existing?.children === null) {
        loadChildren(node.id)
      }
    }
    setExpanded(next)
  }

  function toggleSelect(node: BrowseNode) {
    const next = new Set(selectedPaths)
    if (next.has(node.id)) {
      next.delete(node.id)
    } else {
      next.add(node.id)
    }
    onSelectionChange(next)
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500" />
        <span className="ml-2 text-sm text-harbor-text-secondary">
          Loading repository contents...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-red-400">{error}</p>
        <button
          onClick={loadRoots}
          className="mt-2 text-xs text-emerald-400 hover:text-emerald-300"
        >
          Retry
        </button>
      </div>
    )
  }

  if (roots.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-harbor-text-secondary">No files found.</p>
      </div>
    )
  }

  return (
    <div className="border border-harbor-border rounded-lg overflow-hidden">
      <div className="max-h-80 overflow-y-auto">
        {roots.map((root) => (
          <TreeNodeRow
            key={root.node.id}
            data={root}
            depth={0}
            expanded={expanded}
            selectedPaths={selectedPaths}
            onToggleExpand={toggleExpand}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>
      {selectedPaths.size > 0 && (
        <div className="px-3 py-2 border-t border-harbor-border bg-harbor-surface-raised/50">
          <p className="text-xs text-harbor-text-secondary">
            {selectedPaths.size} item{selectedPaths.size !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  )
}

// ── Tree node row ─────────────────────────────────────────────────

interface TreeNodeRowProps {
  data: TreeNodeData
  depth: number
  expanded: Set<string>
  selectedPaths: Set<string>
  onToggleExpand: (node: BrowseNode) => void
  onToggleSelect: (node: BrowseNode) => void
}

function TreeNodeRow({
  data,
  depth,
  expanded,
  selectedPaths,
  onToggleExpand,
  onToggleSelect,
}: TreeNodeRowProps) {
  const { node, children, loading } = data
  const isSelected = selectedPaths.has(node.id)
  const isExpanded = expanded.has(node.id)
  const isFolder = node.type === 'folder' || node.type === 'repository' || node.type === 'branch'

  return (
    <>
      <div
        className={`flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-harbor-surface-raised/50 transition-colors cursor-pointer ${isSelected ? 'bg-emerald-500/10' : ''}`}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        {isFolder ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(node)
            }}
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-harbor-text-muted hover:text-harbor-text"
          >
            {loading ? (
              <span className="w-3 h-3 border border-harbor-text-muted border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </button>
        ) : (
          <span className="w-4 h-4" />
        )}

        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(node)}
          className="flex-shrink-0 w-3.5 h-3.5 rounded border-harbor-border bg-harbor-surface-raised text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
          onClick={(e) => e.stopPropagation()}
        />

        {isFolder ? (
          <svg
            className="w-4 h-4 text-harbor-text-muted flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 text-harbor-text-muted flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        )}

        <span
          className={`truncate ${isSelected ? 'text-harbor-text' : 'text-harbor-text-secondary'}`}
        >
          {node.name}
        </span>

        {node.type === 'file' && node.size != null && (
          <span className="text-xs text-harbor-text-muted ml-auto flex-shrink-0">
            {formatFileSize(node.size)}
          </span>
        )}
      </div>

      {isFolder && isExpanded && children && children.length > 0 && (
        <div>
          {children.map((child) => (
            <TreeNodeRow
              key={child.node.id}
              data={child}
              depth={depth + 1}
              expanded={expanded}
              selectedPaths={selectedPaths}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </>
  )
}

// ── Immutable tree helpers ─────────────────────────────────────────

function findNode(roots: TreeNodeData[], id: string): TreeNodeData | null {
  for (const root of roots) {
    if (root.node.id === id) return root
    if (root.children) {
      const found = findNode(root.children, id)
      if (found) return found
    }
  }
  return null
}

function setNodeLoading(roots: TreeNodeData[], id: string, loading: boolean): TreeNodeData[] {
  return roots.map((root) => {
    if (root.node.id === id) return { ...root, loading }
    if (root.children) return { ...root, children: setNodeLoading(root.children, id, loading) }
    return root
  })
}

function setNodeChildren(
  roots: TreeNodeData[],
  id: string,
  children: TreeNodeData[]
): TreeNodeData[] {
  return roots.map((root) => {
    if (root.node.id === id) return { ...root, children, loading: false }
    if (root.children) return { ...root, children: setNodeChildren(root.children, id, children) }
    return root
  })
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
